import { useEffect } from "react"
import { applyPublishingRules } from "@/lib/applyPublishingRules"
import { ChoiceField } from "@/components/forms/ChoiceField"
import { Form } from "@/components/forms/Form"
import { RadioField } from "@/components/forms/RadioField"
import { Textarea } from "@/components/forms/Textarea"
import type { LicenseData } from "@/hooks/useLicenseStore"
import { useStrings } from "@/hooks/useStrings"
import { strings } from "@/strings"
import type { UseFormReturn } from "react-hook-form"
import { LicenseLogo } from "@/components/common/LicenseLogo"

type PublishingProps = {
  form: UseFormReturn<LicenseData>
}

export const ReportingFields = ({ form }: PublishingProps) => {
  const additional = form.watch("reportingRequirements") === "Yes"

  return (  
    <>
      <RadioField
        form={form}
        name="reportingRequirements"
        label="Reporting requirements"
        description="Would you like to add some information or examples regarding what type of aggregate reporting is or is not allowed? "
        options={[strings.option.yes || '',   strings.option.no || '']}
      />      

      {additional ? 
        <>
          <Textarea 
            form={form}
            name="reportingTypesPermitted"
            label="Aggregate reporting types"
            description="What are some examples of the types of aggregate reporting allowed/not allowed?"
          />
        </> : <></>}

    </>
  )
}


export const Publishing = ({ form }: PublishingProps) => {
  const strings = useStrings()

  const publishing = form.watch("publishingPermitted") === "yes"
  const reporting = !!form.watch("publishingRequirements")?.reporting
  const reportingRequirements = form.watch("reportingRequirements")
  const reportingTypesPermitted = form.watch("reportingTypesPermitted")

  useEffect(() => {
    const current = form.getValues()
    const next = applyPublishingRules(current)
    for (const field of ["reportingRequirements", "reportingTypesPermitted"]) {
      if (current[field] !== next[field]) {
        form.setValue(field, next[field], { shouldDirty: true, shouldValidate: true })
      }
    }
  }, [form, publishing, reporting, reportingRequirements, reportingTypesPermitted])

  return (
    <Form form={form}>

      <ChoiceField
        form={form}
        behaviour="radio"
        name="publishingPermitted"
        label="Publishing allowed *"
        options={[
          {
            title: strings.option.yes,
            description: "Publication and dissemination of results from working with these data is permitted",
            value: "yes",
            icon: <LicenseLogo logo="publication-yes" />
          },
          {
            title: strings.option.no,
            description: "Publication and dissemination of results from working with these data is not permitted",
            value: "no",
            icon: <LicenseLogo logo="publication-no" />
          },
        ]}
      />

      {publishing
        ? <>
          <ChoiceField
            form={form}
            behaviour="checkbox"
            name="publishingRequirements"
            className="lg:grid-cols-3"
            label="Publishing requirements"
            description="What are the requirements for publishing and disseminating results from working with these data? "
            options={[
              {title: 'Embargo', description: 'Publication and dissemination of results from working with these data are permitted after an embargo period', value: 'embargo', icon: <LicenseLogo logo="publication-embargo" />},
              {title: 'Review',  description: 'Publication and dissemination of results from working with these data are permitted after review and approval of the proposed publication by the data provider', value: 'review', icon: <LicenseLogo logo="publication-review" />},
              {title: 'Reporting', description: 'Reporting is permitted of aggregate data derived from these data, for example in dashboards or monitoring and surveillance reporting.', value: 'reporting', icon: <LicenseLogo logo="aggregated-data-reporting" />}
            ]}
          />
          {reporting && <ReportingFields form={form}/>}
        </>
        : <></>
      }


    </Form>
  )
}
